import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { AddProductMenuWebComponent } from '../component/AddProductMenuWebComponent';
import { AddProductMenuView } from '../view/AddProductMenuView';
import { templateAddProductMenu } from '../view/template';
import { ADD_PRODUCT_EVENT, CLOSE_ADD_PRODUCT_MENU_EVENT } from '../types/types';
import _ from 'lodash';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { ActionEnum } from '../types/ActionEnum';
import { RemoveProductConfirmationWindow } from '../../removeProductConfirmationWindow/RemoveProductConfirmationWindow';
import { LicenseChangeMessageType } from '../../licensingTab/LicensingEnums';
import { LicenseChangeEdrPopUp } from '../../licenseChangePopUp/LicenseChangeEdrPopUp';
import { LicenseChangeCompanionPopUp } from '../../licenseChangePopUp/LicenseChangeCompanionPopUp';
import { LicenseChangeIntegrityMonitorPopUp } from '../../licenseChangePopUp/LicenseChangeIntegrityMonitorPopUp';
import { LicenseChangeNadAndEraAddonPopUp } from '../../licenseChangePopUp/LicenseChangeNadAndEraAddonPopUp';
import { LicenseChangeWebAccessAndDeviceControlAddonPopUp } from '../../licenseChangePopUp/LicenseChangeWebAccessAndDeviceControlAddonPopUp';

export class AddProductMenuController extends BaseWebComponentController {
  public component: AddProductMenuWebComponent;
  public view: AddProductMenuView;
  public viewConstructor: typeof AddProductMenuView;

  constructor(component: AddProductMenuWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: AddProductMenuView,
      viewTemplate: templateAddProductMenu,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$addProductButton,
        event: 'click',
        callback: this.preAddProductAction,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeAddProductSideMenu,
      },
    ];
  }

  public async reloadMenu() {
    await this.render();

    // reset pre paid license form
    this.view.dom.$prePaidForm.resetLicensePrePaidForm();

    this.hideAddProductError();
  }

  private closeAddProductSideMenu() {
    this.component.dispatchEvent(
      new CustomEvent(CLOSE_ADD_PRODUCT_MENU_EVENT, {
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private async preAddProductAction() {
    if (this.view.viewData.action === ActionEnum.changeProduct && this.view.dom.$prePaidForm.isLicenseKeyFieldEmpty()) {
      this.showRemoveProductDialogWindow();
    } else {
      await this.addProduct();
    }
  }

  private async addProduct() {
    this.view.dom.$addProductButton.showSpinner = true;
    this.view.dom.$addProductButton.disabled = true;
    this.view.dom.$cancelButton.disabled = true;

    try {
      // hide backend error when submitting
      this.hideAddProductError();

      const response = await this.dependencies.api.clients.Company.AddLicenseProduct(this.view.viewData.company._id, {
        key: _.get(this.view.dom.$prePaidForm.value, ['specifics', 'licenses', 0, 'key'], null),
        mdrContactInformation: _.get(this.view.dom.$prePaidForm.value, ['specifics', 'mdrContactInformation'], null),
      }).toPromise();

      if (_.isNumber(response.messageType)) {
        this.createAndShowPopUpAfterLicenseChange(response.messageType);
      } else {
        this.onSuccessAddLicenseProduct();
      }
    } catch (error) {
      this.showAddProductError(error);
    } finally {
      this.view.dom.$addProductButton.showSpinner = false;
      this.view.dom.$addProductButton.disabled = false;
      this.view.dom.$cancelButton.disabled = false;
    }
  }

  private createAndShowPopUpAfterLicenseChange(type: LicenseChangeMessageType) {
    switch (type) {
      case LicenseChangeMessageType.Edr:
        this.createAndShowEdrPopUpAfterLicenseChange();
        break;
      case LicenseChangeMessageType.Companion:
        this.createAndShowCompanionPopUpAfterLicenseChange();
        break;
      case LicenseChangeMessageType.IntegrityMonitor:
        this.createAndShowIntegrityMonitorPopUpAfterLicenseChange();
        break;
      case LicenseChangeMessageType.NadAndEraAddon:
        this.createAndShowNadAndEraAddonPopUpAfterLicenseChange();
        break;
      case LicenseChangeMessageType.WebAccessAndDeviceControlAddon:
        this.createAndShowWebAccessAndDeviceControlAddonPopUpAfterLicenseChange();
        break;
      default:
        this.onSuccessAddLicenseProduct();
        break;
    }
  }

  private createAndShowEdrPopUpAfterLicenseChange() {
    const confirmCallback = async () => {
      await this.dependencies.api.clients.Licenses.ReconfigureAfterLicenseChangeEdrPopUp(
        this.view.viewData.company._id
      ).toPromise();
      this.onSuccessAddLicenseProduct();
    };

    const cancelCallback = () => {
      this.onSuccessAddLicenseProduct();
    };

    LicenseChangeEdrPopUp.showLicenseChangeEdrPopUp(this.dependencies.translateFn, cancelCallback, confirmCallback);
  }

  private createAndShowCompanionPopUpAfterLicenseChange() {
    const shouldButtonsBeVisible = this.view.viewData.company.specifics.rights.manageNetworks;

    const confirmCallback = async () => {
      await this.dependencies.api.clients.Licenses.ReconfigureAfterLicenseChangeCompanionPopUp().toPromise();

      // When pre-paid form doesn't exist, it means that the current window is already closed.
      // (The current window already closed in ReconfigureAfterLicenseChangeCompanionPopUp method for my company, because windowDialog is set in ExtJs.)
      if (!this.view.dom.$prePaidForm) {
        return;
      }

      // For Edit, windowDialog is not set in ExtJs (because edit component is started from Nirvana), so we need to close it manually (onSuccessAddLicenseProduct is called).
      this.onSuccessAddLicenseProduct(true);
    };

    const cancelCallback = () => {
      this.onSuccessAddLicenseProduct();
    };

    LicenseChangeCompanionPopUp.showLicenseChangeCompanionPopUp(
      this.dependencies.translateFn,
      shouldButtonsBeVisible,
      cancelCallback,
      confirmCallback
    );
  }

  private createAndShowIntegrityMonitorPopUpAfterLicenseChange() {
    const confirmCallback = async () => {
      await this.dependencies.api.clients.Licenses.ReconfigureAfterLicenseChangeIntegrityMonitorPopUp(
        this.view.viewData.company._id
      ).toPromise();
      this.onSuccessAddLicenseProduct();
    };

    const cancelCallback = () => {
      this.onSuccessAddLicenseProduct();
    };

    LicenseChangeIntegrityMonitorPopUp.showLicenseChangeIntegrityMonitorPopUp(
      this.dependencies.translateFn,
      cancelCallback,
      confirmCallback
    );
  }

  private createAndShowNadAndEraAddonPopUpAfterLicenseChange() {
    const confirmCallback = async () => {
      await this.dependencies.api.clients.Licenses.ReconfigureAfterLicenseChangeNadAndEraAddonPopUp(
        this.view.viewData.company._id
      ).toPromise();
      this.onSuccessAddLicenseProduct();
    };

    const cancelCallback = () => {
      this.onSuccessAddLicenseProduct();
    };

    LicenseChangeNadAndEraAddonPopUp.showLicenseChangeNadAndEraAddonPopUp(
      this.dependencies.translateFn,
      cancelCallback,
      confirmCallback
    );
  }

  private createAndShowWebAccessAndDeviceControlAddonPopUpAfterLicenseChange() {
    const confirmCallback = async () => {
      await this.dependencies.api.clients.Licenses.ReconfigureAfterLicenseChangeWebAccessAndDeviceControlAddonPopUp(
        this.view.viewData.company._id
      ).toPromise();
      this.onSuccessAddLicenseProduct();
    };

    const cancelCallback = () => {
      this.onSuccessAddLicenseProduct();
    };

    LicenseChangeWebAccessAndDeviceControlAddonPopUp.showLicenseChangeWebAccessAndDeviceControlAddonPopUp(
      this.dependencies.translateFn,
      cancelCallback,
      confirmCallback
    );
  }

  private onSuccessAddLicenseProduct(shouldClosePanel: boolean = false) {
    const message = this.view.dom.$prePaidForm.isLicenseKeyFieldEmpty()
      ? this.dependencies.translateFn('The product was removed successfully.')
      : this.dependencies.translateFn('The product was added successfully.');

    this.showSuccessToast(message);
    this.closeAddProductSideMenu();

    this.component.dispatchEvent(
      new CustomEvent(ADD_PRODUCT_EVENT, {
        detail: {
          license: {
            isAddon: _.get(this.view.dom.$prePaidForm.value, ['specifics', 'licenses', 0, 'isAddon'], null),
            productId: _.get(this.view.dom.$prePaidForm.value, ['specifics', 'licenses', 0, 'productId'], null),
          },
          shouldClosePanel: shouldClosePanel,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private showAddProductError(error: Error) {
    let errorMessage;

    if (error['exceptionType'] === 'Exceptions\\NoInternetConnection') {
      errorMessage = this.dependencies.translateFn(
        'The licensing server is temporarily unavailable. Please try again later.'
      );
    } else {
      errorMessage = error?.message || this.translator.getGenericErrorMessage();
    }

    this.view.dom.$backendErrorContainer.innerHTML = `<span>${errorMessage}</span>`;
    toggleVisibility(this.view.dom.$backendErrorContainer, true);

    this.view.dom.$innerContainer.scrollTo({
      behavior: 'smooth',
      top: this.view.dom.$innerContainer.scrollHeight,
    });
  }

  private hideAddProductError() {
    toggleVisibility(this.view.dom.$backendErrorContainer, false);
  }

  private showSuccessToast(message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message,
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showRemoveProductDialogWindow() {
    const removeCallback = () => {
      this.addProduct().then().catch(console.error);
    };
    RemoveProductConfirmationWindow.showRemoveProductConfirmationWindow(
      this.dependencies.translateFn,
      null,
      removeCallback
    );
  }
}
